import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Plus, Star } from 'lucide-react';

import {
  addClientNote,
  createContact,
  getClient,
  listActivities,
  listContacts,
  type ClientDetail,
  type ClientLifecycleStage,
} from '@/api/clients.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Tabs } from '@/components/ui/Tabs';
import { useToast } from '@/hooks/useToast';

type BadgeVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info';
const STAGE_VARIANT: Record<ClientLifecycleStage, BadgeVariant> = {
  lead: 'neutral',
  qualified: 'info',
  onboarding: 'warning',
  live: 'success',
  dormant: 'neutral',
  lost: 'danger',
};
const titleCase = (s: string): string => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export function ClientDetailPage() {
  const { id = '' } = useParams();
  const [tab, setTab] = useState('overview');

  const clientQuery = useQuery({ queryKey: ['client', id], queryFn: () => getClient(id), enabled: !!id });
  const client = clientQuery.data;

  if (clientQuery.isLoading) return <div className="p-6 text-kw-muted">Loading…</div>;
  if (!client) {
    return (
      <div className="p-6">
        <Card className="p-6 text-kw-muted">Client not found.</Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <Link
        to="/clients"
        className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:underline"
      >
        <ArrowLeft className="size-4" /> Clients
      </Link>

      <header className="flex flex-wrap items-center gap-3">
        <h1 className="typo-h1">{client.name}</h1>
        <Badge variant={STAGE_VARIANT[client.lifecycleStage]}>{titleCase(client.lifecycleStage)}</Badge>
        <span className="text-[13px] text-kw-muted">{client.clientType.name}</span>
      </header>

      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'contacts', label: 'Contacts' },
          { id: 'timeline', label: 'Timeline' },
        ]}
        activeTab={tab}
        onChange={setTab}
      />

      {tab === 'overview' && <OverviewTab client={client} />}
      {tab === 'contacts' && <ContactsTab clientId={id} roleLabels={client.clientType.contactRoleLabels} />}
      {tab === 'timeline' && <TimelineTab clientId={id} />}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <div className="text-[12px] uppercase tracking-wide text-kw-muted">{label}</div>
      <div className="mt-0.5 text-[14px] text-kw-shark">{value ?? '—'}</div>
    </div>
  );
}

function OverviewTab({ client }: { client: ClientDetail }) {
  return (
    <Card className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-3">
      <Field label="Website" value={client.website} />
      <Field label="Phone" value={client.phone} />
      <Field label="Source" value={client.source} />
      <Field label="Deal value / mo" value={client.dealValueMonthly ? `$${client.dealValueMonthly}` : null} />
      <Field label="Email domains" value={client.emailDomains.join(', ') || null} />
      <Field label="ShipEdge key" value={client.shipedgeClientKey} />
      {client.notes && (
        <div className="col-span-full">
          <div className="text-[12px] uppercase tracking-wide text-kw-muted">Notes</div>
          <p className="mt-1 whitespace-pre-wrap text-[14px] text-kw-shark">{client.notes}</p>
        </div>
      )}
    </Card>
  );
}

function ContactsTab({ clientId, roleLabels }: { clientId: string; roleLabels: string[] }) {
  const [adding, setAdding] = useState(false);
  const contactsQuery = useQuery({
    queryKey: ['client-contacts', clientId],
    queryFn: () => listContacts(clientId),
  });
  const contacts = contactsQuery.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() => {
            setAdding(true);
          }}
        >
          <Plus className="size-4" /> Add contact
        </Button>
      </div>
      {contacts.length === 0 ? (
        <Card className="p-5 text-kw-muted">No contacts yet.</Card>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {contacts.map((c) => (
            <Card key={c.id} className="p-4">
              <div className="flex items-center gap-2">
                <span className="font-medium text-kw-shark">
                  {c.firstName} {c.lastName}
                </span>
                {c.isPrimary && <Star className="size-3.5 fill-kw-warning-text text-kw-warning-text" />}
                {c.roleLabel && <Badge variant="neutral">{titleCase(c.roleLabel)}</Badge>}
              </div>
              {c.title && <div className="text-[13px] text-kw-muted">{c.title}</div>}
              <div className="mt-2 space-y-1 text-[13px] text-kw-muted">
                <div className="flex items-center gap-1.5">
                  <Mail className="size-3.5" /> {c.email}
                </div>
                {c.phone && (
                  <div className="flex items-center gap-1.5">
                    <Phone className="size-3.5" /> {c.phone}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
      {adding && (
        <AddContactModal
          clientId={clientId}
          roleLabels={roleLabels}
          onClose={() => {
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

function AddContactModal({
  clientId,
  roleLabels,
  onClose,
}: {
  clientId: string;
  roleLabels: string[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { show } = useToast();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [roleLabel, setRoleLabel] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      createContact(clientId, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        ...(roleLabel ? { roleLabel } : {}),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['client-contacts', clientId] });
      show('Contact added.', { variant: 'success' });
      onClose();
    },
    onError: () => {
      show('Could not add the contact.', { variant: 'danger' });
    },
  });

  const canSubmit =
    firstName.trim() && lastName.trim() && /.+@.+\..+/.test(email.trim()) && !mutation.isPending;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (canSubmit) mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title="Add contact">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
            }}
            placeholder="First name"
          />
          <Input
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
            }}
            placeholder="Last name"
          />
        </div>
        <Input
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          placeholder="Email"
          type="email"
        />
        {roleLabels.length > 0 && (
          <select
            value={roleLabel}
            onChange={(e) => {
              setRoleLabel(e.target.value);
            }}
            className="w-full rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[14px]"
          >
            <option value="">Role (optional)…</option>
            {roleLabels.map((r) => (
              <option key={r} value={r}>
                {titleCase(r)}
              </option>
            ))}
          </select>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            Add
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function TimelineTab({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const { show } = useToast();
  const [note, setNote] = useState('');
  const activitiesQuery = useQuery({
    queryKey: ['client-activities', clientId],
    queryFn: () => listActivities(clientId),
  });

  const mutation = useMutation({
    mutationFn: () => addClientNote(clientId, note.trim()),
    onSuccess: async () => {
      setNote('');
      await qc.invalidateQueries({ queryKey: ['client-activities', clientId] });
      show('Note added.', { variant: 'success' });
    },
    onError: () => {
      show('Could not add the note.', { variant: 'danger' });
    },
  });

  const rows = activitiesQuery.data?.rows ?? [];

  return (
    <div className="space-y-3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (note.trim() && !mutation.isPending) mutation.mutate();
        }}
        className="flex gap-2"
      >
        <Input
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
          placeholder="Add a note…"
        />
        <Button type="submit" disabled={!note.trim() || mutation.isPending}>
          Add
        </Button>
      </form>
      {rows.length === 0 ? (
        <Card className="p-5 text-kw-muted">No activity yet.</Card>
      ) : (
        <Card className="divide-y divide-kw-border p-0">
          {rows.map((a) => (
            <div key={a.id} className="flex items-start justify-between gap-3 px-4 py-3">
              <div>
                <div className="text-[14px] text-kw-shark">{a.title}</div>
                <div className="text-[12px] text-kw-muted">{titleCase(a.type)}</div>
              </div>
              <time className="shrink-0 text-[12px] text-kw-muted">
                {new Date(a.createdAt).toLocaleString()}
              </time>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
